import {
  ALL_LAYOUT_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  FLEX_ARGS,
  GRID_ARGS,
  Metadata,
  POSITIONING_ARGS,
  pick,
} from '../../../utils/spds/metadata';
import { INITIAL } from '../../../utils/spds/constants/css-layout';
import { bool, or } from 'macro-decorators';
import { element } from 'ember-element-helper';
import { htmlSafe } from '@ember/template';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { verifyHtmlTag } from '../../../utils/spds/html-verification';
import Super from './base.gjs';
import styles from './container.module.scss';

const METADATA = Metadata.define({
  description: `
Generic container component

### Customization

By default, the rendered HTML element is a \`div\` but any valid HTML element can be rendered by providing the \`@tag\` argument.

### Styling

The default CSS \`display\` for the container is \`flex\` with a \`flex-direction\` of \`row\`. It can be changed to \`inline-flex\` by
providing the \`@inline\` argument. The \`flex-direction\` can be changed to \`column\` by providing the \`@column\` argument.
Additional styling can be added to the container by assigning custom CSS classes to the component through the HTML \`class\` attribute
as normal.

Similarly, there are some arguments which can be used to quickly apply height and width sizing to restrict the size of the container:

* \`@fitContent\`: this restricts both the height and width of the container to fit its content
* \`@fitContentHeight\`: adds the size restriction to just the height of the container
* \`@fitContentWidth\`: adds the size restriction to just the width of the container

### Positioning

The \`@absolute\` and \`@fixed\` arguments will apply CSS classes that set the container's positioning to those respective values.
Specific X and Y positioning can be applied by adding a custom CSS class that provides that data.

### Discarding clicks

In the case that the content of the container should not be clickable for some reason, there is a preconfigured modifier which can be applied
to the container automatically by passing the \`@discardClick\` argument.
  `,
  extends: [
    CONTAINER_ARGS,
    POSITIONING_ARGS,
    ALL_LAYOUT_ARGS,
    pick(COMPONENT_STATE_ARGS, ['error']),
    pick(CONTAINER_ARGS, ['inline', 'tag']),
  ],
  meta: {
    content: Arg.string({ description: 'HTML content to render inside the container' }),
    discardClick: Arg.boolean({ description: 'Prevent click events from propagating' }),
    inputContainer: Arg.boolean({ description: 'Apply input container styling' }),
  },
  name: 'Container',
});

// Cached prop names to avoid Object.keys() on every instance access
const FLEX_PROP_NAMES = Object.keys(FLEX_ARGS.meta);
const GRID_PROP_NAMES = Object.keys(GRID_ARGS.meta);

export default class SpdsCoreContainer extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-container';
  classesToMap = [
    'absolute',
    'column',
    'error',
    'fitContentHeight',
    'fitContentWidth',
    'fixed',
    'grid',
    'inline',
    'inputContainer',
    'reverse',
    'subgrid',
  ];
  styles = styles;

  @service fastboot;

  @bool('args.fitContent') fitContent;
  @bool('args.fitContentHeight') fitContentH;
  @bool('args.fitContentWidth') fitContentW;
  @bool('args.discardClick') shouldDiscardClick;

  @or('fitContent', 'fitContentH') fitContentHeight;
  @or('fitContent', 'fitContentW') fitContentWidth;

  get alignContent() {
    return this.argOrDefault('alignContent');
  }

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get alignSelf() {
    return this.argOrDefault('alignSelf');
  }

  get column() {
    return this.args.column && !this.grid;
  }

  get content() {
    return htmlSafe(this.args.content);
  }

  get defaultGap() {
    return this.constructor.metadata.getDefault('gap');
  }

  get flex() {
    const mainAxisConstrained = this.column ? this.fitContentHeight : this.fitContentWidth;

    return mainAxisConstrained ? INITIAL : this.argOrDefault('flex');
  }

  get flexWrap() {
    return this.argOrDefault('flexWrap');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get grid() {
    return this.argOrDefault('grid');
  }

  get gridArea() {
    return this.args.gridArea ?? this.args.area ?? this.constructor.metadata.getDefault('gridArea');
  }

  get gridAutoColumns() {
    return this.argOrDefault('gridAutoColumns');
  }

  get gridAutoFlow() {
    return (
      this.args.gridAutoFlow ??
      this.args.autoFlow ??
      this.constructor.metadata.getDefault('gridAutoFlow')
    );
  }

  get gridAutoRows() {
    return this.argOrDefault('gridAutoRows');
  }

  get gridColumn() {
    return this.argOrDefault('gridColumn');
  }

  get gridRow() {
    return this.args.gridRow ?? this.args.row ?? this.constructor.metadata.getDefault('gridRow');
  }

  get gridTemplateAreas() {
    return (
      this.args.gridTemplateAreas ??
      this.args.areas ??
      this.constructor.metadata.getDefault('gridTemplateAreas')
    );
  }

  get gridTemplateColumns() {
    return (
      this.args.gridTemplateColumns ??
      this.args.columns ??
      this.constructor.metadata.getDefault('gridTemplateColumns')
    );
  }

  get gridTemplateRows() {
    return (
      this.args.gridTemplateRows ??
      this.args.rows ??
      this.constructor.metadata.getDefault('gridTemplateRows')
    );
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  get justifyItems() {
    return this.argOrDefault('justifyItems');
  }

  get justifySelf() {
    return this.argOrDefault('justifySelf');
  }

  get order() {
    return this.argOrDefault('order');
  }

  get propsToMap() {
    return this.grid ? GRID_PROP_NAMES : FLEX_PROP_NAMES;
  }

  get reverse() {
    return this.args.reverse && !this.grid;
  }

  get tag() {
    let ELEMENT_TAG_DEFAULT = this.constructor.metadata.getDefault('tag');
    let { tag = ELEMENT_TAG_DEFAULT } = this.args;

    if (this.fastboot?.isFastBoot) return tag;

    return verifyHtmlTag(ELEMENT_TAG_DEFAULT, tag);
  }

  ignoreClick = evt => {
    evt.stopPropagation();
  };

  setup = () => {
    if (!(this.shouldDiscardClick && this.baseElement)) return;

    this.baseElement.addEventListener('click', this.ignoreClick);

    registerDestructor(this, () => {
      this.baseElement.removeEventListener('click', this.ignoreClick);
    });
  };

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag
        class={{this.classes}}
        data-test-spds-container={{this.tag}}
        id={{this.id}}
        style={{this.customProperties}}
        {{this.setupComponent}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @content}}
          {{this.content}}
        {{else}}
          {{yield}}
        {{/if}}
      </Tag>
    {{/let}}
  </template>
}

export const CORE_CONTAINER_METADATA = SpdsCoreContainer.metadata;
